/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type { ParsedTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';
import { ReverseDeviceSensor } from '@/lib/sensors/catalog/reverseDeviceSensor';
import type { TrafficLightSeverity } from '@/lib/overview/overviewCards';

export type TimeSeriesAggregation = 'avg' | 'sum' | 'last' | 'last-14' | 'first' | 'min' | 'max';
export type TimeSeriesTrafficLightSeverity = TrafficLightSeverity;
export type TimeSeriesTrafficLightOperator = 'lt' | 'lte' | 'gt' | 'gte';

export interface TimeSeriesLookback {
    amount: number;
    toleranceDays?: number;
    unit: 'day' | 'month' | 'year';
}

export interface TimeSeriesLookbackDifference {
    currentDate: string;
    referenceDate: string;
    value: number;
}

export interface TimeSeriesTrafficLightRule {
    severity: TimeSeriesTrafficLightSeverity;
    title?: string;
    value: number;
    when: TimeSeriesTrafficLightOperator;
}

export interface TimeSeriesTrafficLightColors {
    danger?: string;
    implausible?: string;
    success?: string;
    warning?: string;
}

export interface TimeSeriesTrafficLightConfig {
    colors?: TimeSeriesTrafficLightColors;
    fallback?: TimeSeriesTrafficLightSeverity;
    labels?: Partial<Record<TimeSeriesTrafficLightSeverity, string>>;
    rules: TimeSeriesTrafficLightRule[];
}

export interface TimeSeriesTrafficLightLegendCondition {
    operator: TimeSeriesTrafficLightOperator;
    value: number;
}

const DEFAULT_TIME_SERIES_TRAFFIC_LIGHT_LABELS: Record<TimeSeriesTrafficLightSeverity, string> = {
    danger: 'Error',
    implausible: 'Implausible',
    success: 'OK',
    warning: 'Warning',
};

export interface TimeSeriesValueLabelsConfig {
    fallback?: string | null;
    values: Record<string, string>;
}

export function resolveTimeSeriesTrafficLightLabel(
    severity: TimeSeriesTrafficLightSeverity,
    trafficLight: TimeSeriesTrafficLightConfig | undefined,
    defaultLabel: string,
): string {
    return trafficLight?.labels?.[severity] ?? defaultLabel;
}

export function resolveTimeSeriesTrafficLightLabelDefault(
    severity: TimeSeriesTrafficLightSeverity,
    trafficLight?: TimeSeriesTrafficLightConfig,
): string {
    return resolveTimeSeriesTrafficLightLabel(
        severity,
        trafficLight,
        DEFAULT_TIME_SERIES_TRAFFIC_LIGHT_LABELS[severity],
    );
}

export interface TimeSeriesCalcContext {
    values: Record<string, number | null>;
}

export type TimeSeriesCalc = (
    context: TimeSeriesCalcContext,
) => Record<string, number | null>;

export function createTimeSeriesCalcValuesWithAliases(
    values: Record<string, number | null>,
): Record<string, number | null> {
    const aliasCounts = new Map<string, number>();

    Object.keys(values).forEach((key) => {
        const alias = new ReverseDeviceSensor(key).getSensorType(true);

        if (!alias || alias === key) {
            return;
        }

        aliasCounts.set(alias, (aliasCounts.get(alias) ?? 0) + 1);
    });

    const valuesWithAliases = { ...values };

    Object.entries(values).forEach(([key, value]) => {
        const alias = new ReverseDeviceSensor(key).getSensorType(true);

        if (!alias || alias === key || aliasCounts.get(alias) !== 1) {
            return;
        }

        valuesWithAliases[alias] = value;
    });

    return valuesWithAliases;
}

export function normalizeTimeSeriesUrn(urn: string | string[]): string {
    return Array.isArray(urn) ? urn.join(',') : urn;
}

export function findTimeSeriesValueColumn(
    parsedTimeSeries: ParsedTimeSeriesCsv,
    series?: string,
): string | null {
    if (series) {
        return (
            parsedTimeSeries.plotYAxisColumns.find((column) => column.includes(series)) ??
            null
        );
    }

    return parsedTimeSeries.plotYAxisColumns[0] ?? null;
}

export function aggregateTimeSeriesValues(
    values: number[],
    aggregation: TimeSeriesAggregation,
): number | null {
    const finiteValues = values.filter((value) => Number.isFinite(value));

    if (finiteValues.length === 0) {
        return null;
    }

    switch (aggregation) {
        case 'avg':
            return (
                finiteValues.reduce((sum, value) => sum + value, 0) / finiteValues.length
            );
        case 'sum':
            return finiteValues.reduce((sum, value) => sum + value, 0);
        case 'last':
            return finiteValues[finiteValues.length - 1];
        case 'last-14':
            return finiteValues.slice(-14).reduce((sum, value) => sum + value, 0);
        case 'first':
            return finiteValues[0];
        case 'min':
            return Math.min(...finiteValues);
        case 'max':
            return Math.max(...finiteValues);
        default:
            return null;
    }
}

export function getTimeSeriesAggregationDate(
    values: number[],
    dates: string[],
    aggregation: TimeSeriesAggregation,
): string | null {
    const indexedFiniteValues = values
        .map((value, index) => ({
            date: dates[index] ?? '',
            index,
            value,
        }))
        .filter(({ value }) => Number.isFinite(value));

    if (indexedFiniteValues.length === 0) {
        return null;
    }

    switch (aggregation) {
        case 'avg':
        case 'sum':
        case 'last':
        case 'last-14':
            return indexedFiniteValues[indexedFiniteValues.length - 1]?.date || null;
        case 'first':
            return indexedFiniteValues[0]?.date || null;
        case 'min': {
            const minEntry = indexedFiniteValues.reduce((currentMin, entry) =>
                entry.value < currentMin.value ? entry : currentMin,
            );

            return minEntry.date || null;
        }
        case 'max': {
            const maxEntry = indexedFiniteValues.reduce((currentMax, entry) =>
                entry.value > currentMax.value ? entry : currentMax,
            );

            return maxEntry.date || null;
        }
        default:
            return null;
    }
}

function parseTimeSeriesDate(value: string): Date | null {
    const timestamp = Date.parse(value.replace(' ', 'T'));

    return Number.isNaN(timestamp) ? null : new Date(timestamp);
}

function subtractTimeSeriesLookback(date: Date, lookback: TimeSeriesLookback): Date | null {
    if (!Number.isInteger(lookback.amount) || lookback.amount < 1) {
        return null;
    }

    const targetDate = new Date(date);

    switch (lookback.unit) {
        case 'day':
            targetDate.setDate(targetDate.getDate() - lookback.amount);
            break;
        case 'month':
            targetDate.setMonth(targetDate.getMonth() - lookback.amount);
            break;
        case 'year':
            targetDate.setFullYear(targetDate.getFullYear() - lookback.amount);
            break;
        default:
            return null;
    }

    return targetDate;
}

/**
 * Returns the difference between the latest value and the closest permitted
 * reference value. Earlier values take precedence over later values.
 */
export function resolveTimeSeriesLookbackDifference(
    values: number[],
    dates: string[],
    lookback: TimeSeriesLookback,
): TimeSeriesLookbackDifference | null {
    const points = values
        .map((value, index) => ({
            date: dates[index] ?? '',
            timestamp: parseTimeSeriesDate(dates[index] ?? ''),
            value,
        }))
        .filter((point): point is { date: string; timestamp: Date; value: number } => (
            point.timestamp !== null && Number.isFinite(point.value)
        ))
        .sort((left, right) => left.timestamp.getTime() - right.timestamp.getTime());
    const currentPoint = points.at(-1);

    if (!currentPoint) {
        return null;
    }

    const referenceDate = subtractTimeSeriesLookback(currentPoint.timestamp, lookback);

    if (!referenceDate) {
        return null;
    }

    const toleranceDays = lookback.toleranceDays;

    if (toleranceDays !== undefined && (Number.isNaN(toleranceDays) || toleranceDays < 0)) {
        return null;
    }

    const toleranceMs = toleranceDays === undefined
        ? Number.POSITIVE_INFINITY
        : toleranceDays * 24 * 60 * 60 * 1000;
    const earlierReferencePoint = points
        .filter((point) => (
            point.timestamp.getTime() <= referenceDate.getTime() &&
            referenceDate.getTime() - point.timestamp.getTime() <= toleranceMs
        ))
        .at(-1);
    const laterReferencePoint = toleranceDays === undefined
        ? undefined
        : points.find((point) => (
            point.timestamp.getTime() > referenceDate.getTime() &&
            point.timestamp.getTime() - referenceDate.getTime() <= toleranceMs
        ));
    const referencePoint = earlierReferencePoint ?? laterReferencePoint;

    if (!referencePoint) {
        return null;
    }

    return {
        currentDate: currentPoint.date,
        referenceDate: referencePoint.date,
        value: currentPoint.value - referencePoint.value,
    };
}

export function getTimeSeriesLookbackDifference(
    values: number[],
    dates: string[],
    lookback: TimeSeriesLookback,
): number | null {
    return resolveTimeSeriesLookbackDifference(values, dates, lookback)?.value ?? null;
}

function matchesTrafficLightRule(
    value: number,
    rule: TimeSeriesTrafficLightRule,
): boolean {
    switch (rule.when) {
        case 'lt':
            return value < rule.value;
        case 'lte':
            return value <= rule.value;
        case 'gt':
            return value > rule.value;
        case 'gte':
            return value >= rule.value;
        default:
            return false;
    }
}

export function resolveTimeSeriesTrafficLightSeverity(
    value: number | null,
    trafficLight?: TimeSeriesTrafficLightConfig,
): TimeSeriesTrafficLightSeverity | null {
    if (value === null || !trafficLight) {
        return null;
    }

    const matchingRule = trafficLight.rules.find((rule) =>
        matchesTrafficLightRule(value, rule),
    );

    return matchingRule?.severity ?? trafficLight.fallback ?? null;
}

export function resolveTimeSeriesTrafficLightSeverityDefault(
    value: number | null,
    trafficLight: TimeSeriesTrafficLightConfig | undefined,
    defaultSeverity: TimeSeriesTrafficLightSeverity = 'implausible',
): TimeSeriesTrafficLightSeverity {
    return resolveTimeSeriesTrafficLightSeverity(value, trafficLight) ?? defaultSeverity;
}

export function resolveTimeSeriesTrafficLightStatusLabel(
    value: number | null,
    trafficLight: TimeSeriesTrafficLightConfig | undefined,
    defaultSeverity: TimeSeriesTrafficLightSeverity = 'implausible',
): string {
    return resolveTimeSeriesTrafficLightLabelDefault(
        resolveTimeSeriesTrafficLightSeverityDefault(value, trafficLight, defaultSeverity),
        trafficLight,
    );
}

export function resolveTimeSeriesTrafficLightTitle(
    value: number | null,
    trafficLight?: TimeSeriesTrafficLightConfig,
): string | null {
    if (value === null || !trafficLight) {
        return null;
    }

    const matchingRule = trafficLight.rules.find((rule) =>
        matchesTrafficLightRule(value, rule),
    );

    return matchingRule?.title ?? null;
}

export function resolveTimeSeriesTrafficLightLegendCondition(
    severity: TimeSeriesTrafficLightSeverity,
    trafficLight?: TimeSeriesTrafficLightConfig,
): TimeSeriesTrafficLightLegendCondition | null {
    if (!trafficLight) {
        return null;
    }

    const matchingRule = trafficLight.rules.find((rule) => rule.severity === severity);

    if (matchingRule) {
        return {
            operator: matchingRule.when,
            value: matchingRule.value,
        };
    }

    if (trafficLight.fallback !== severity || trafficLight.rules.length === 0) {
        return null;
    }

    const onlyLowerBoundRules = trafficLight.rules.every((rule) =>
        rule.when === 'lt' || rule.when === 'lte',
    );

    if (onlyLowerBoundRules) {
        const boundaryRule = trafficLight.rules.reduce((current, rule) =>
            rule.value > current.value ? rule : current,
        );

        return {
            operator: boundaryRule.when === 'lt' ? 'gte' : 'gt',
            value: boundaryRule.value,
        };
    }

    const onlyUpperBoundRules = trafficLight.rules.every((rule) =>
        rule.when === 'gt' || rule.when === 'gte',
    );

    if (onlyUpperBoundRules) {
        const boundaryRule = trafficLight.rules.reduce((current, rule) =>
            rule.value < current.value ? rule : current,
        );

        return {
            operator: boundaryRule.when === 'gt' ? 'lte' : 'lt',
            value: boundaryRule.value,
        };
    }

    return null;
}

export function resolveTimeSeriesValueLabel(
    value: number | null,
    valueLabels?: TimeSeriesValueLabelsConfig,
): string | null {
    if (value === null || !valueLabels) {
        return null;
    }

    return valueLabels.values[String(value)] ?? valueLabels.fallback ?? null;
}

export function formatTimeSeriesNumericValue(
    value: number,
    locale: string,
    fractionDigits?: number,
    trimFractionDigitsForIntegers = false,
): string {
    const resolvedFractionDigits = trimFractionDigitsForIntegers && Number.isInteger(value)
        ? 0
        : fractionDigits;

    return value.toLocaleString(locale, {
        minimumFractionDigits: resolvedFractionDigits ?? 0,
        maximumFractionDigits: resolvedFractionDigits ?? 2,
    });
}
